import { useMemo, useState } from 'react'
import { magasin } from '../lib/client/store.ts'
import {
  emojiProduit,
  useMagasin,
  useRayons,
  type Article,
  type Lieu,
  type Produit,
  type Peremption,
} from '../lib/client/hooks.ts'
import { Row, EnTeteGroupe } from '../components/Row.tsx'
import { Croix } from '../components/Icone.tsx'
import { chercher } from '../lib/search.ts'
import { DetailArticle } from './DetailArticle.tsx'
import { BadgeDlc, joursAvant } from '../components/BadgeDlc.tsx'
import { annoncer } from '../components/Toast.tsx'
import type { EtatStock } from '../lib/ops/index.ts'

/*
 * « En stock » ne veut PAS dire « tout ».
 *
 * Les articles épuisés restaient dans la liste principale, et l'inventaire
 * accumulait ce qu'on n'a pas — « il y en a que je n'achète que de temps en
 * temps ». Un inventaire qui mélange ce qu'on possède et ce qu'on ne
 * possède plus cesse de répondre à la seule question qu'on lui pose en
 * ouvrant l'app : qu'est-ce que j'ai ?
 *
 * Ils ne sont pas supprimés pour autant — leur onglet les garde à portée,
 * avec leurs dates et leurs notes, et ils reviennent en stock dès qu'on les
 * rachète.
 */
type Filtre = 'stock' | 'epuise' | 'perime'

export function Inventaire({ onOuvrirAjout }: { onOuvrirAjout: (saisie?: string) => void }) {
  const { etat } = useMagasin()
  const rayons = useRayons()
  const [filtre, setFiltre] = useState<Filtre>('stock')
  /*
   * Les articles qu'on vient de passer à « épuisé » RESTENT affichés.
   *
   * Sans ça la ligne disparaît sous le doigt à l'instant du tap : la
   * suivante remonte à sa place et reçoit le tap d'après, et surtout la
   * jauge ne peut plus revenir en arrière — son cycle est en_stock →
   * bientôt fini → épuisé → en_stock, donc le retour passe par la ligne
   * qu'on vient de faire disparaître. C'est la même raison qui interdit de
   * réordonner les lignes cochées en mode magasin.
   *
   * La liste se nettoie à la prochaine ouverture de l'écran.
   */
  const [gardes, setGardes] = useState<Set<string>>(() => new Set())
  const [detail, setDetail] = useState<string | null>(null)
  const [recherche, setRecherche] = useState('')

  const lieux = useMemo(
    () => ([...etat.locations.values()] as unknown as Lieu[])
      .filter((l) => !(l as unknown as { deletedAt?: unknown }).deletedAt)
      .sort((a, b) => a.sortOrder - b.sortOrder),
    [etat],
  )
  const produits = useMemo(
    () => new Map(([...etat.catalogItems.values()] as unknown as Produit[]).map((p) => [p.id, p])),
    [etat],
  )
  const peremptions = useMemo(() => {
    const m = new Map<string, Peremption[]>()
    for (const p of [...etat.expiries.values()] as unknown as Peremption[]) {
      if ((p as unknown as { deletedAt?: unknown }).deletedAt) continue
      const l = m.get(p.inventoryItemId) ?? []
      l.push(p)
      m.set(p.inventoryItemId, l)
    }
    for (const l of m.values()) l.sort((a, b) => a.date.localeCompare(b.date))
    return m
  }, [etat])

  const articles = useMemo(
    () =>
      ([...etat.inventoryItems.values()] as unknown as Article[]).filter(
        (a) => !(a as unknown as { deletedAt?: unknown }).deletedAt,
      ),
    [etat],
  )

  const visibles = articles.filter((a) => {
    if (filtre === 'epuise') return a.state === 'epuise'
    if (filtre === 'perime') {
      const d = peremptions.get(a.id)?.[0]
      // Une date ne concerne que ce qu'on a encore.
      return a.state !== 'epuise' && d ? joursAvant(d.date, magasin.maintenant()) <= 3 : false
    }
    return a.state !== 'epuise' || gardes.has(a.id)
  })

  const epuises = articles.filter((a) => a.state === 'epuise').length

  /*
   * LA RECHERCHE IGNORE LE FILTRE ET L'EMPLACEMENT.
   *
   * On ne cherche pas pour explorer, on cherche parce qu'on a un produit en
   * tête et qu'on veut savoir ce qu'il en est. Restreindre à l'onglet
   * courant répondrait « rien » pour un article simplement épuisé, et
   * laisserait croire qu'il n'a jamais existé — le pire résultat possible
   * pour une recherche. Le groupement par emplacement disparaît aussi :
   * quand on cherche une chose précise, les intertitres sont du bruit.
   *
   * On passe par `chercher`, comme l'ajout rapide : accents, pluriels et
   * synonymes se comportent donc partout pareil. Deux recherches qui ne
   * trouvent pas la même chose dans la même app, c'est une app qu'on cesse
   * de croire.
   */
  const resultats = useMemo(() => {
    const q = recherche.trim()
    if (!q) return null
    const avecNom = articles.map((a) => {
      const p = produits.get(a.catalogItemId)
      return {
        article: a,
        produit: p,
        nom: p?.name ?? 'Article',
        search: p?.search ?? '',
        tier: p?.tier ?? 1,
      }
    })
    /*
     * Pas de plafond : l'écran promet « tout l'inventaire », et tronquer en
     * silence ferait mentir le compte affiché en en-tête. Quelques centaines
     * de lignes filtrées en mémoire ne coûtent rien ; une liste qui s'arrête
     * sans le dire coûte la confiance qu'on a dans la recherche.
     */
    return chercher(avecNom, q, avecNom.length)
  }, [recherche, articles, produits])

  function changerEtat(article: Article, suivant: EtatStock) {
    magasin.dispatch('item.setState', { id: article.id, state: suivant })

    if (suivant !== 'epuise') {
      setGardes((g) => {
        if (!g.has(article.id)) return g
        const n = new Set(g)
        n.delete(article.id)
        return n
      })
      return
    }

    setGardes((g) => new Set(g).add(article.id))

    /*
     * L'ajout aux courses est PROPOSÉ, là où il était automatique.
     *
     * Le toast le propose à l'endroit et à l'instant du geste, donc sans
     * rien coûter à qui veut vraiment racheter — un tap, au même endroit.
     * Ce qui change, c'est que ne rien faire ne remplit plus la liste.
     */
    const nom = produits.get(article.catalogItemId)?.name ?? 'Article'
    annoncer(`${nom} épuisé`, {
      label: 'Ajouter aux courses',
      faire: () =>
        magasin.dispatch('list.add', {
          id: magasin.id(),
          catalogItemId: article.catalogItemId,
          reason: 'stock',
        }),
    })
  }

  return (
    <div className="pb-24">
      <div className="sticky top-0 z-20 bg-canvas/80 backdrop-blur-xl" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex h-[var(--size-bar)] items-center justify-between px-4">
          <h1 className="font-serif text-title text-ink">Inventaire</h1>
          <span className="tnum text-meta text-muted">
            {articles.filter((a) => a.state !== 'epuise').length}
          </span>
        </div>
        {/*
          Le segmenté DISPARAÎT pendant la recherche, et ce n'est pas une
          économie de place : la recherche ignore le filtre, donc le laisser
          affiché montrerait un onglet actif qui ne décrit pas ce qu'on voit.
          Un contrôle qui ment sur l'état est pire qu'un contrôle absent.
        */}
        {!recherche.trim() && (
          <Segmented
            valeur={filtre}
            onChange={setFiltre}
            options={[
              ['stock', 'En stock'],
              // Le compte est AFFICHÉ sur l'onglet : sans lui, rien ne dit
              // qu'il s'y trouve quelque chose, et les épuisés deviendraient
              // invisibles au lieu d'être simplement rangés ailleurs.
              ['epuise', epuises > 0 ? `Épuisés (${epuises})` : 'Épuisés'],
              ['perime', 'Bientôt périmé'],
            ]}
          />
        )}

        <div className="relative mx-4 mb-2">
          <input
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Chercher dans l’inventaire…"
            /* Le correcteur français massacre « chiffonnade » et
               « rillettes ». inputMode=search donne la bonne touche Entrée,
               et le champ est à 17 px : en dessous de 16, iOS zoome au focus
               et ne dézoome jamais. */
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            autoComplete="off"
            inputMode="search"
            enterKeyHint="search"
            aria-label="Chercher dans l’inventaire"
            className="h-[40px] w-full rounded-sm bg-surface-sunk pl-9 pr-10 text-field text-ink outline-none"
          />
          <svg
            aria-hidden
            viewBox="0 0 24 24"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          {recherche && (
            <button
              type="button"
              onClick={() => setRecherche('')}
              aria-label="Effacer la recherche"
              className="pressable absolute right-1 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center text-muted"
            >
              <Croix className="size-4" />
            </button>
          )}
        </div>
      </div>

      <div className="px-3 pt-2">
        {resultats && (
          <>
            {resultats.length === 0 ? (
              <div className="mt-16 flex flex-col items-center gap-2 px-8 text-center">
                <p className="text-head text-ink">Rien ne correspond.</p>
                <p className="text-meta text-muted">
                  Cherché dans tout l’inventaire, épuisés compris.
                </p>
                {/* Le terme cherché PART AVEC. Sans ça le bouton promet
                    d'ajouter « origan » puis ouvre un champ vide : il faut
                    retaper le mot qu'on vient d'écrire, juste au-dessus. */}
                <button
                  type="button"
                  onClick={() => {
                    onOuvrirAjout(recherche.trim())
                    setRecherche('')
                  }}
                  className="pressable mt-3 h-11 rounded-sm bg-accent px-5 text-body text-accent-ink"
                >
                  Ajouter « {recherche.trim()} »
                </button>
              </div>
            ) : (
              <section className="mb-5">
                <EnTeteGroupe titre="Résultats" compte={resultats.length} />
                <div className="overflow-hidden rounded-md shadow-row">
                  {resultats.map(({ article: a, produit: p }) => {
                    const dlc = peremptions.get(a.id)?.[0]
                    const lieu = lieux.find((l) => l.id === a.locationId)
                    /*
                     * On montre l'EMPLACEMENT en sous-titre, puisqu'on vient
                     * de supprimer le groupement qui le portait. Sans lui, un
                     * résultat ne dit pas où aller chercher le produit — ce
                     * qui est pourtant la question posée neuf fois sur dix.
                     */
                    const sous = [lieu?.name, a.note].filter(Boolean).join(' · ')
                    return (
                      <Row
                        key={a.id}
                        nom={p?.name ?? 'Article'}
                        emoji={emojiProduit(p, rayons)}
                        etat={a.state}
                        detail={sous || null}
                        badge={
                          dlc ? <BadgeDlc date={dlc.date} maintenant={magasin.maintenant()} /> : null
                        }
                        onEtat={(e) => changerEtat(a, e)}
                        onOuvrir={() => setDetail(a.id)}
                      />
                    )
                  })}
                </div>
              </section>
            )}
          </>
        )}

        {!resultats && visibles.length === 0 && <Vide filtre={filtre} onAjouter={() => onOuvrirAjout()} />}

        {!resultats && lieux.map((lieu) => {
          const dedans = visibles
            .filter((a) => a.locationId === lieu.id)
            .sort((a, b) => {
              const na = produits.get(a.catalogItemId)?.name ?? ''
              const nb = produits.get(b.catalogItemId)?.name ?? ''
              return na.localeCompare(nb, 'fr')
            })
          if (dedans.length === 0) return null

          return (
            <section key={lieu.id} className="mb-5">
              <EnTeteGroupe titre={lieu.name} emoji={lieu.emoji} compte={dedans.length} />
              <div className="overflow-hidden rounded-md shadow-row">
                {dedans.map((a) => {
                  const p = produits.get(a.catalogItemId)
                  const dlc = peremptions.get(a.id)?.[0]
                  return (
                    <Row
                      key={a.id}
                      nom={p?.name ?? 'Article'}
                      emoji={emojiProduit(p, rayons)}
                      etat={a.state}
                      detail={a.note}
                      badge={dlc ? <BadgeDlc date={dlc.date} maintenant={magasin.maintenant()} /> : null}
                      onEtat={(e) => changerEtat(a, e)}
                      onOuvrir={() => setDetail(a.id)}
                    />
                  )
                })}
              </div>
            </section>
          )
        })}
      </div>

      <DetailArticle articleId={detail} onFermer={() => setDetail(null)} />
    </div>
  )
}

function Segmented<T extends string>({
  valeur,
  onChange,
  options,
}: {
  valeur: T
  onChange: (v: T) => void
  options: Array<[T, string]>
}) {
  const index = options.findIndex(([v]) => v === valeur)
  return (
    /* `relative` : le pouce est en `absolute` et se calait sinon sur
       l'en-tête collant, le premier ancêtre positionné au-dessus. Ça
       fonctionnait par accident. */
    <div className="relative mx-4 mb-2 flex h-[34px] rounded-sm bg-surface-sunk p-[3px]">
      {/* Le pouce se DÉPLACE (translateX), il n'apparaît pas : c'est un
          mouvement à l'écran, pas une entrée. */}
      <div
        aria-hidden
        className="absolute h-7 rounded-[7px] bg-surface shadow-row"
        style={{
          // Calé sur le conteneur, maintenant qu'il est le référentiel.
          left: 3,
          top: 3,
          width: `calc((100% - 6px) / ${options.length})`,
          transform: `translateX(calc(${index} * 100%))`,
          transition: 'transform 220ms var(--ease-in-out)',
        }}
      />
      {options.map(([v, label]) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          aria-pressed={v === valeur}
          className={`pressable relative z-10 flex-1 rounded-[7px] text-meta ${
            v === valeur ? 'font-semibold text-ink' : 'text-muted'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

function Vide({ filtre, onAjouter }: { filtre: Filtre; onAjouter: () => void }) {
  const textes: Record<Filtre, [string, string]> = {
    stock: ['Rien en stock pour le moment.', 'Ajoute ce que tu as dans le frigo.'],
    epuise: ['Rien d’épuisé.', 'Les articles épuisés viennent ici, et sont suggérés aux courses.'],
    perime: ['Rien ne périme bientôt.', 'Les dates se règlent article par article.'],
  }
  const [titre, sous] = textes[filtre]
  return (
    <div className="mt-16 flex flex-col items-center gap-2 px-8 text-center">
      <p className="text-head text-ink">{titre}</p>
      <p className="text-meta text-muted">{sous}</p>
      {filtre === 'stock' && (
        <button
          type="button"
          onClick={onAjouter}
          className="pressable mt-3 h-11 rounded-sm bg-accent px-5 text-body text-accent-ink"
        >
          Ajouter un article
        </button>
      )}
    </div>
  )
}
